<template>
  <div class="app">
    <!-- 路由视图 -->
    <router-view v-slot="{ Component }">
      <transition name="slide-fade">
        <component :is="Component" />
      </transition>
    </router-view>
    
    <!-- 底部导航栏 -->
    <van-tabbar class="control" v-if="showTabbar" v-model="activeTab" @change="onTabChange">
      <van-tabbar-item 
        v-for="item in tabbarItems" 
        :key="item.name"
        :name="item.name" 
        :icon="item.icon"
        :title="item.title"
        :to="item.path"
      >{{ item.title }}</van-tabbar-item>
    </van-tabbar>

    <!-- 全局加载指示器 -->
    <div v-if="globalStore.loading" class="global-loading">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-text">加载中...</p>
      </div>
    </div>

    <!-- 公告弹窗 -->
    <van-popup v-model:show="showNotice" position="center" :closeable="false" @close="onNoticeClose">
      <div class="notice-popup">
        <h3 class="notice-title">{{ notice.notice_title }}</h3>
        <div class="notice-content" v-html="notice.notice_content"></div>
        <div class="notice-footer">
          <van-button type="primary" block round @click="onNoticeClose">我知道了</van-button>
        </div>
      </div>
    </van-popup>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { getStorage, setStorage } from './utils/storage'
import { CRM } from './api/models/crm'
import { useGlobalStore } from './store/global'
import { useUserStore } from './store/user'
import { getTabbarItems, tabbarItems as defaultTabbarItems } from './config/tabbar'

const globalStore = useGlobalStore()
const userStore = useUserStore()

const router = useRouter()
const route = useRoute()

const activeTab = ref('home')

const tabbarItems = computed(() => {
  const role = userStore.userInfo?.user_role
  if (role) {
    return getTabbarItems(role)
  }
  return defaultTabbarItems
})

const showTabbar = computed(() => {
  return route.meta?.showInTabbar === true
})

watch(() => route.path, (path) => {
  const matchedTab = tabbarItems.value.find(item => item.path === path)
  if (matchedTab) {
    activeTab.value = matchedTab.name
    return
  }
  const primaryPath = path.split('/')[1] ? `/${path.split('/')[1]}` : '/' 
  const fallbackTab = tabbarItems.value.find(item => item.path === primaryPath)
  if (fallbackTab) {
    activeTab.value = fallbackTab.name
  }
}, { immediate: true })

const onTabChange = (name) => {
  const matchedTab = tabbarItems.value.find(item => item.name === name)
  if (matchedTab) {
    router.push(matchedTab.path)
  }
}

const showNotice = ref(false)
const notice = ref({
  notice_title: '',
  notice_content: ''
})

const fetchNotice = async () => {
  try {
    const res = await CRM.getPopupAnnouncement()
    if (!res.data || res.data.is_read || !res.data.notice_content) return;
    
    notice.value = res.data
    const isRead = getStorage(`notice_${notice.value.notice_id}_read`, false)
    if (!isRead && !res.data.is_read) {
      showNotice.value = true
    }
  } catch (error) {
    console.error('获取公告失败:', error)
  }
}

const onNoticeClose = async () => {
  try {
    const noticeId = notice.value.notice_id
    await CRM.readNotice(noticeId)
    setStorage(`notice_${noticeId}_read`, true)
  } catch (error) {
    console.error('标记公告已读失败:', error)
  } finally {
    showNotice.value = false
  }
}

watch(() => userStore.isLoggedIn, (newVal) => {
  if (newVal) {
    fetchNotice()
  }
})

onMounted(() => {
  fetchNotice()
})
</script>
